import map from 'lodash/map';

import { doRequest } from '@r1-frontend/do-request';
import calculate from '@r1-frontend/api-domru/api-tariffs/v1/tariff/calculate';
import { ResponseDto } from '@r1-frontend/api-domru/api-tariffs/v1/tariff/calculate/dto/ResponseDto';

import { TUseToaster } from '@r1-frontend/ui-react/experimental/Toaster';
import { isResponseCanceled } from '@r1-frontend/shared/helpers/isResponseCanceled';

import { initialized, setLoading, setStep } from '~/src/features/tariffBuilder/actions';
import { getAbortControllerRecreater } from '~/src/features/tariffBuilder/actions/thunk/helpers/getAbortControllerRecreater';
import BaseInfoToast from '~/src/features/tariffBuilder/components/toast/BaseInfoToast';
import RecalculateFallDown from '~/src/features/tariffBuilder/components/toast/RecalculateFallDown';
import RouterNotSupplySpeed from '~/src/features/tariffBuilder/components/toast/RouterNotSupplySpeed';
import { TCtxDispatch } from '~/src/features/tariffBuilder/Ctx';
import { getUpdatedValue } from '~/src/features/tariffBuilder/helpers/getUpdatedValue';
import { prepareRouters } from '~/src/features/tariffBuilder/helpers/transformer';
import prepareBaseSubscription from '~/src/features/tariffBuilder/helpers/transformer/prepareBaseSubscription';
import prepareDecoders from '~/src/features/tariffBuilder/helpers/transformer/prepareDecoders';
import prepareDescription from '~/src/features/tariffBuilder/helpers/transformer/prepareDescription';
import prepareSpeed from '~/src/features/tariffBuilder/helpers/transformer/prepareSpeed';
import prepareTvSubscription from '~/src/features/tariffBuilder/helpers/transformer/prepareTvSubscription';
import { TInitialState } from '~/src/features/tariffBuilder/reducer';
import getRecalculateBody from '~/src/features/tariffBuilder/selectors/getRecalculateBody';
import isBaseTvSelected from '~/src/features/tariffBuilder/selectors/isBaseTvSelected';
import { EEntity } from '~/src/features/tariffBuilder/types/EEntity';
import { ESteps } from '~/src/features/tariffBuilder/types/ESteps';

const infoToastId = 'infoToastId';
const routerToastId = 'routerNotSupplySpeed';

const createAbortController = getAbortControllerRecreater();

const recalculate = (toaster?: TUseToaster) => async(dispatch: TCtxDispatch, getState: () => TInitialState) => {
    const state = getState();

    const onLinkClick = () => {
        dispatch(setStep(ESteps.Equipment));
        toaster && toaster.remove(routerToastId);
    };

    if (!state.loading) {
        toaster && toaster.add(<BaseInfoToast message={'Пересчитываем цены, это может занять пару секунд.'}/>, true, infoToastId);
        dispatch(setLoading(true));
    }

    const calculateBody = getRecalculateBody(state);

    const abortController = createAbortController();

    const requestWithSignal = {
        /**
         * If current tariff is defined, then user is authorized
         */
        ...calculate(calculateBody, !!state.currentTariff),
        signal: abortController.signal,
    };

    const response = await doRequest.apiTariffs<ResponseDto>(requestWithSignal);

    if (response.isSuccess) {
        toaster && toaster.remove(infoToastId);
        dispatch(setLoading(false));

        const incomingRoutersId = map(response.payload.entities.routers, router => Number(router.id));
        const filteredRouters = state.selectedEntityIds[EEntity.routers].filter(id => incomingRoutersId.includes(Number(id)));
        if (filteredRouters.length < state.selectedEntityIds[EEntity.routers].length) {
            toaster && toaster.add(<RouterNotSupplySpeed onLinkClick={onLinkClick}/>, false, routerToastId);
        }
        const selectedVases = response.payload.selectedVases || [];
        const preparedSpeed = prepareSpeed(response.payload.speed, isBaseTvSelected(state));

        const currentTariffSpeedIndex = preparedSpeed.findIndex(item => item.speed === state.currentTariff?.speed);
        const selectedSpeed = ~currentTariffSpeedIndex
            ? preparedSpeed[currentTariffSpeedIndex].id
            // If there are speed limits, we should choose the first option
            : (state[EEntity.speed].length != response.payload.speed.length ? 0 : state.selectedSpeed);

        dispatch(initialized({
            [EEntity.speed]: preparedSpeed,
            [EEntity.onlineCinemas]: getUpdatedValue(state[EEntity.onlineCinemas], response.payload.entities.onlineCinemas, selectedVases, prepareBaseSubscription),

            [EEntity.tv]: getUpdatedValue(state[EEntity.tv], response.payload.entities.tv, selectedVases, prepareTvSubscription),
            [EEntity.additional]: getUpdatedValue(state[EEntity.additional], response.payload.entities.entertainments, selectedVases, prepareBaseSubscription),

            [EEntity.antivirus]: getUpdatedValue(state[EEntity.antivirus], response.payload.entities.antivirus, selectedVases, prepareBaseSubscription),
            [EEntity.security]: getUpdatedValue(state[EEntity.security], response.payload.entities.security, selectedVases, prepareBaseSubscription),
            [EEntity.routers]: getUpdatedValue(state[EEntity.routers], response.payload.entities.routers, [], prepareRouters),
            // after speed changed we should to remove selected routers that do not satisfy condition by a speed
            selectedEntityIds: {
                ...state.selectedEntityIds,
                [EEntity.services]: selectedVases,
                [EEntity.routers]: filteredRouters,
            },

            [EEntity.decoders]: prepareDecoders(state[EEntity.decoders], response.payload.entities?.decoders),

            selectedSpeed,

            entityDescription: prepareDescription(response.payload.entities),
            discount: response.payload.discount,
            salePackageId: response.payload.salePackageId,
            planId: response.payload.planId,

            // reset current tariff speed after first initialization, if exists
            currentTariff: state.currentTariff?.speed
                ? {
                    ...state.currentTariff,
                    speed: null,
                }
                : state.currentTariff,
        }));
    } else {
        if (isResponseCanceled(response.error.message)) {
            return;
        }

        // @todo move from this action to a container.
        toaster && toaster.add(<RecalculateFallDown/>);
        toaster && toaster.remove(infoToastId);
        dispatch(setLoading(false));
    }
};

export default recalculate;
